/**
 * PlanCheckSections — plan-check sections.
 *
 * Exports:
 *   DeltaRevisionsSection       — Delta Revisions (Airtable, read-only), mounted on the PERMIT
 *                                 (a delta can reference several Agency Reviews at once — see
 *                                 getDeltaRevisionsForProject() in services/permits.js)
 *   AgencyCorrespondenceSection — plan_check_letters + embedded comments (Supabase), per review
 *   StakeholderInputSection    — stakeholder_inputs (Supabase), per review
 *
 * Caches on ctx (reset in PermitsSection on project change):
 *   ctx.permitDeltaCache           — undefined|null|[]   Airtable deltas, whole project
 *   ctx.pcLetterCache[reviewId]    — undefined|null|[]   letters with embedded comments[]
 *   ctx.stakeholderCache[reviewId] — undefined|null|[]   stakeholder inputs
 */

import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { confirmDialog, promptDialog, alertDialog } from './Dialog.jsx'
import ContactPicker from './crm/ContactPicker.jsx'
import DrawingSheetList from './DrawingSheetList.jsx'
import { resolveMilestonePhasePrefix } from './MilestoneSections.jsx'
import {
  getAirtableLettersForReview, getAirtableCommentsForLetter, updateAirtableCommentResponse,
  getRevisionMilestoneCandidates, linkDeltaRevisionToMilestone,
} from '../services/permits.js'
import { getMilestoneBasicInfo } from '../services/milestones.js'
import { getMilestoneSheetRevisions } from '../services/sheets.js'
import {
  getPlanCheckData,
  createPlanCheckLetter, updatePlanCheckLetter, deletePlanCheckLetter,
  createPlanCheckComment, updatePlanCheckComment, deletePlanCheckComment,
  createStakeholderInput, updateStakeholderInput, deleteStakeholderInput,
  generatePCResponsePdf,
  getIntakeForReview, discardPCIntake,
} from '../services/planCheck.js'
import PlanCheckIntakeModal from './PlanCheckIntakeModal.jsx'
import ElevateToThreadModal from './ElevateToThreadModal.jsx'

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function fmtDate(d) {
  if (!d) return ''
  const dp = (d || '').split(/[-T]/)
  if (dp.length >= 3) return MONTHS[parseInt(dp[1]) - 1] + ' ' + parseInt(dp[2]) + ', ' + dp[0]
  return d
}

/** First value of a possibly-array Airtable field. */
function first(v) {
  if (Array.isArray(v)) return v.length ? v[0] : null
  return v == null ? null : v
}

function commentStatus(c) {
  if (c.cleared)             return { emoji: '✅', label: 'Cleared',     color: '#1D9E75' }
  if (c.response_finalized)  return { emoji: '🔄', label: 'Responded',   color: '#2563eb' }
  if (c.response_text)       return { emoji: '🔄', label: 'In Progress', color: '#7c3aed' }
  return                            { emoji: '⏳', label: 'Open',        color: '#d97706' }
}

function groupByDiscipline(comments) {
  const groups = {}
  for (const c of comments) {
    const disc = (c.discipline || '').trim() || 'General'
    if (!groups[disc]) groups[disc] = []
    groups[disc].push(c)
  }
  return Object.entries(groups).sort(([a], [b]) => {
    if (a === 'General') return 1
    if (b === 'General') return -1
    return a.localeCompare(b)
  })
}

function loadingRow() {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span>
        </div>
      </div>
    </div>
  )
}

function emptyRow(text) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>{text}</span>
        </div>
      </div>
    </div>
  )
}

/* ── Label chip (key: value) ────────────────────────────────────────────── */

function InfoRow({ label, value }) {
  if (!value) return null
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginRight: 6 }}>
            {label}
          </span>
          <span className="wf-node-text">{value}</span>
        </div>
      </div>
    </div>
  )
}

/* ── Comment row ─────────────────────────────────────────────────────────── */

function CommentRow({ comment, letterId, reviewId, projectId }) {
  const menu = useActionMenu()
  const [elevateOpen, setElevateOpen] = useState(false)
  const isReadOnly = comment._source === 'airtable'
  const st = commentStatus(comment)
  const isRedline = comment.type === 'redline'
  const title = isRedline
    ? 'Redline Comment' + (comment.subject ? ': ' + comment.subject : '')
    : ('Comment' + (comment.comment_number ? ' ' + comment.comment_number : '') + (comment.subject ? ': ' + comment.subject : ''))

  function bustCache() {
    if (ctx.pcLetterCache) ctx.pcLetterCache[reviewId] = undefined
    window.dispatchEvent(new Event('outline-render'))
  }

  // Elevate to Thread (2026-09): for the minority of comments that can't be closed by revising —
  // they need an active chase. Airtable-sourced comments can be elevated too (the thread carries the
  // record), but the "elevated" marker only round-trips on Supabase comments, which have the column.
  async function handleElevated(threadId) {
    setElevateOpen(false)
    if (!isReadOnly) {
      try { await updatePlanCheckComment(comment.id, { elevated_thread_id: threadId }) } catch (_) { /* thread still exists */ }
    }
    bustCache()
  }

  function handleOpenThread() {
    ctx.zoomToSection('thread-detail:' + comment.elevated_thread_id, { root: 'threads' })
  }

  async function handleEdit() {
    const num = await promptDialog({ title: isRedline ? 'Edit Redline' : 'Edit Comment', message: 'Number (e.g. 1, 1.1 — optional):', defaultValue: comment.comment_number || '' })
    if (num === null) return
    const subject = await promptDialog({ title: isRedline ? 'Edit Redline' : 'Edit Comment', message: 'Subject:', defaultValue: comment.subject || '' })
    if (subject === null) return
    const commentText = await promptDialog({ title: isRedline ? 'Edit Redline' : 'Edit Comment', message: "Agency's comment text:", defaultValue: comment.comment_text || '', multiline: true })
    if (commentText === null) return
    const responseText = await promptDialog({ title: isRedline ? 'Edit Redline' : 'Edit Comment', message: 'Our response:', defaultValue: comment.response_text || '', multiline: true })
    if (responseText === null) return
    const discipline = await promptDialog({ title: isRedline ? 'Edit Redline' : 'Edit Comment', message: 'Discipline (e.g. Architectural — leave blank for General):', defaultValue: comment.discipline || '' })
    if (discipline === null) return
    try {
      await updatePlanCheckComment(comment.id, {
        comment_number: num || null,
        subject: subject || null,
        comment_text: commentText || null,
        response_text: responseText || null,
        discipline: discipline || null,
      })
      bustCache()
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  async function handleToggleCleared() {
    try {
      await updatePlanCheckComment(comment.id, { cleared: !comment.cleared })
      bustCache()
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  async function handleToggleFinalized() {
    try {
      await updatePlanCheckComment(comment.id, { response_finalized: !comment.response_finalized })
      bustCache()
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  async function handleDelete() {
    if (!(await confirmDialog({ title: 'Delete Comment?', message: 'Delete "' + (title || 'this comment') + '"?', confirmLabel: 'Delete', danger: true }))) return
    try {
      await deletePlanCheckComment(comment.id)
      bustCache()
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  // AT comment response editing (writes back to tblvf0AwovOm7616X)
  async function handleEditAtResponse() {
    const responseText = await promptDialog({
      title: isRedline ? 'Edit Redline Response' : 'Edit Response',
      message: 'Our response:',
      defaultValue: comment.response_text || '',
      multiline: true,
    })
    if (responseText === null) return
    try {
      await updateAirtableCommentResponse(comment.id, {
        response: responseText || null,
        responseFinalized: comment.response_finalized,
      })
      bustCache()
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  async function handleToggleAtFinalized() {
    try {
      await updateAirtableCommentResponse(comment.id, {
        response: comment.response_text,
        responseFinalized: !comment.response_finalized,
      })
      bustCache()
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{title || 'Comment'}</span>
          <span className="wf-meta">
            <span className="wf-status-pill" style={{ color: st.color, borderColor: st.color + '33' }}>{st.emoji} {st.label}</span>
            {comment.elevated_thread_id && (
              <span className="wf-date-btn" style={{ cursor: 'pointer', color: '#7c3aed' }} onClick={e => { e.stopPropagation(); handleOpenThread() }}>
                → thread
              </span>
            )}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      {comment.comment_text && (
        <div style={{ paddingLeft: 96, paddingBottom: 2 }}>
          <div className="wf-node-text" style={{ fontSize: 11, color: 'var(--aj-text-mid)', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.45 }}>
            {comment.comment_text}
          </div>
        </div>
      )}
      {comment.response_text && (
        <div style={{ paddingLeft: 96, paddingBottom: 4 }}>
          <div className="wf-node-text" style={{ fontSize: 11, color: '#1D9E75', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.45 }}>
            ↳ {comment.response_text}
          </div>
        </div>
      )}
      {isReadOnly ? (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={handleEditAtResponse}>Edit response</ActionMenuItem>
          <ActionMenuItem onClick={handleToggleAtFinalized}>{comment.response_finalized ? 'Mark response open' : 'Mark response finalized'}</ActionMenuItem>
          <ActionMenuDivider />
          {comment.elevated_thread_id
            ? <ActionMenuItem onClick={handleOpenThread}>Open thread</ActionMenuItem>
            : <ActionMenuItem onClick={() => setElevateOpen(true)}>Elevate to thread…</ActionMenuItem>}
        </ActionMenu>
      ) : (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={handleEdit}>Edit comment</ActionMenuItem>
          <ActionMenuItem onClick={handleToggleFinalized}>{comment.response_finalized ? 'Mark response open' : 'Mark response finalized'}</ActionMenuItem>
          <ActionMenuItem onClick={handleToggleCleared}>{comment.cleared ? 'Reopen comment' : 'Mark cleared'}</ActionMenuItem>
          <ActionMenuDivider />
          {comment.elevated_thread_id
            ? <ActionMenuItem onClick={handleOpenThread}>Open thread</ActionMenuItem>
            : <ActionMenuItem onClick={() => setElevateOpen(true)}>Elevate to thread…</ActionMenuItem>}
          <ActionMenuDivider />
          <ActionMenuItem onClick={handleDelete} danger>Delete comment</ActionMenuItem>
        </ActionMenu>
      )}
      {elevateOpen && (
        <ElevateToThreadModal
          projectId={projectId}
          initialTitle={title}
          initialDescription={[comment.comment_text, comment.response_text ? 'Our response so far: ' + comment.response_text : null].filter(Boolean).join('\n\n')}
          onClose={() => setElevateOpen(false)}
          onElevated={handleElevated}
        />
      )}
    </div>
  )
}

/* ── Discipline group ────────────────────────────────────────────────────── */

function DisciplineGroup({ groupKey, discipline, comments, reviewId, projectId, onAdd, readOnly }) {
  const isCollapsed = wf.collapsed[groupKey] !== false
  const menu = useActionMenu()

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[groupKey] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={groupKey}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--section" />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{discipline}</span>
          <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{comments.length}</span>
        </div>
        {!readOnly && <ActionMenuButton menu={menu} />}
      </div>
      {!readOnly && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={onAdd}>Add comment</ActionMenuItem>
        </ActionMenu>
      )}
      {!isCollapsed && (
        <div className="wf-children">
          {comments.map(c => <CommentRow key={c.id} comment={c} reviewId={reviewId} projectId={projectId} />)}
        </div>
      )}
    </div>
  )
}

/* ── Written Comments section ────────────────────────────────────────────── */

function WrittenCommentsSection({ letter, reviewId, projectId }) {
  const key = 'pc-written:' + letter.id
  const isCollapsed = wf.collapsed[key] !== false
  const readOnly = letter._source === 'airtable'
  const comments = (letter.comments || []).filter(c => c.type === 'letter')
  const menu = useActionMenu()

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  function bustCache() {
    if (ctx.pcLetterCache) ctx.pcLetterCache[reviewId] = undefined
    window.dispatchEvent(new Event('outline-render'))
  }

  async function handleAddComment(discipline) {
    const num = await promptDialog({ title: 'Add Written Comment', message: 'Comment number (e.g. 1, 1.1 — optional):' })
    if (num === null) return
    const subject = await promptDialog({ title: 'Add Written Comment', message: 'Subject:' })
    if (subject === null) return
    const commentText = await promptDialog({ title: 'Add Written Comment', message: "Agency's comment:", multiline: true })
    if (commentText === null) return
    const disc = discipline && discipline !== 'General' ? discipline : await promptDialog({ title: 'Add Written Comment', message: 'Discipline (leave blank for General):' }) || null
    if (disc === null && discipline === undefined) return
    try {
      await createPlanCheckComment(letter.id, { type: 'letter', comment_number: num || null, subject: subject || null, comment_text: commentText || null, discipline: (disc === 'General' ? null : disc) || null })
      bustCache()
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  const groups = groupByDiscipline(comments)

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--section" />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Written Comments</span>
          {comments.length > 0 && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{comments.length}</span>}
        </div>
        {!readOnly && <ActionMenuButton menu={menu} />}
      </div>
      {!readOnly && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => handleAddComment()}>Add written comment</ActionMenuItem>
        </ActionMenu>
      )}
      {!isCollapsed && (
        <div className="wf-children">
          {groups.length === 0 && emptyRow('No written comments yet.')}
          {groups.map(([disc, cmts]) => (
            <DisciplineGroup
              key={disc}
              groupKey={'pc-disc-letter:' + letter.id + ':' + disc}
              discipline={disc}
              comments={cmts}
              reviewId={reviewId}
              projectId={projectId}
              onAdd={!readOnly ? () => handleAddComment(disc) : undefined}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Redlines section ────────────────────────────────────────────────────── */

function RedlinesSection({ letter, reviewId, projectId }) {
  const key = 'pc-redlines:' + letter.id
  const isCollapsed = wf.collapsed[key] !== false
  const readOnly = letter._source === 'airtable'
  const comments = (letter.comments || []).filter(c => c.type === 'redline')
  const menu = useActionMenu()

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  function bustCache() {
    if (ctx.pcLetterCache) ctx.pcLetterCache[reviewId] = undefined
    window.dispatchEvent(new Event('outline-render'))
  }

  async function handleAddRedline(discipline) {
    const subject = await promptDialog({ title: 'Add Redline Comment', message: 'Subject / sheet description:' })
    if (subject === null) return
    const commentText = await promptDialog({ title: 'Add Redline Comment', message: 'Comment / correction noted:', multiline: true })
    if (commentText === null) return
    const disc = discipline && discipline !== 'General' ? discipline : await promptDialog({ title: 'Add Redline Comment', message: 'Discipline (leave blank for General):' }) || null
    if (disc === null && discipline === undefined) return
    try {
      await createPlanCheckComment(letter.id, { type: 'redline', subject: subject || null, comment_text: commentText || null, discipline: (disc === 'General' ? null : disc) || null })
      bustCache()
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  const groups = groupByDiscipline(comments)

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--section" />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Redlines</span>
          {comments.length > 0 && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{comments.length}</span>}
        </div>
        {!readOnly && <ActionMenuButton menu={menu} />}
      </div>
      {!readOnly && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => handleAddRedline()}>Add redline comment</ActionMenuItem>
        </ActionMenu>
      )}
      {!isCollapsed && (
        <div className="wf-children">
          {groups.length === 0 && emptyRow('No redlines yet.')}
          {groups.map(([disc, cmts]) => (
            <DisciplineGroup
              key={disc}
              groupKey={'pc-disc-redline:' + letter.id + ':' + disc}
              discipline={disc}
              comments={cmts}
              reviewId={reviewId}
              projectId={projectId}
              onAdd={!readOnly ? () => handleAddRedline(disc) : undefined}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Letter form modal (date received / linked reviewer contact / response date) ── */

const modalInputStyle = {
  width: '100%', fontFamily: '"DM Mono",monospace', fontSize: 13, color: 'var(--aj-text)',
  padding: '7px 9px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4,
  background: 'var(--aj-bg-subtle)', boxSizing: 'border-box',
}

function modalLabel(text) {
  return (
    <div style={{
      fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 9,
      letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 5,
    }}>
      {text}
    </div>
  )
}

function LetterFormModal({ title, initial, showResponseDate, onSave, onClose }) {
  const [dateReceived, setDateReceived] = useState(initial.date_received || '')
  const [label, setLabel] = useState(initial.label || '')
  const [reviewerContactId, setReviewerContactId] = useState(initial.reviewer_contact_airtable_id || '')
  const [reviewerContactName, setReviewerContactName] = useState(initial.reviewer_contact_name || '')
  const [responseDate, setResponseDate] = useState(initial.response_letter_date || '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  async function handleSave() {
    setSaving(true)
    try {
      await onSave({
        date_received: dateReceived || null,
        label: label || null,
        reviewer_contact_airtable_id: reviewerContactId || null,
        reviewer_contact_name: reviewerContactName || null,
        ...(showResponseDate ? { response_letter_date: responseDate || null } : {}),
      })
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{
        background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)',
        width: 420, maxWidth: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', padding: '16px 20px',
          borderBottom: '0.5px solid var(--aj-border)',
        }}>
          <div style={{
            fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12,
            letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', flex: 1,
          }}>
            {title}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', padding: 4 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            {modalLabel('Date Received')}
            <input type="date" value={dateReceived} onChange={e => setDateReceived(e.target.value)} style={modalInputStyle} />
          </div>
          <div>
            {modalLabel('Label (optional — to tell letters apart)')}
            <input
              type="text" value={label} onChange={e => setLabel(e.target.value)}
              placeholder="e.g. Round 1 redlines, From City Engineer"
              style={{ ...modalInputStyle, fontFamily: '"DM Sans",sans-serif' }}
            />
          </div>
          <div>
            {modalLabel('Plan Check Reviewer')}
            <ContactPicker
              value={reviewerContactId}
              name={reviewerContactName}
              onChange={(id, name) => { setReviewerContactId(id); setReviewerContactName(name) }}
              inputStyle={{ ...modalInputStyle, fontFamily: '"DM Sans",sans-serif' }}
            />
          </div>
          {showResponseDate && (
            <div>
              {modalLabel('Response Date')}
              <input type="date" value={responseDate} onChange={e => setResponseDate(e.target.value)} style={modalInputStyle} />
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '0.5px solid var(--aj-border)' }}>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '8px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ background: '#07272D', border: 'none', borderRadius: 4, padding: '8px 20px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, color: 'white', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/* ── PC Letter Info section ──────────────────────────────────────────────── */

function PCLetterInfoSection({ letter, reviewId }) {
  const key = 'pc-letter-info:' + letter.id
  const isCollapsed = wf.collapsed[key] !== false
  const readOnly = letter._source === 'airtable'
  const menu = useActionMenu()
  const [editOpen, setEditOpen] = useState(false)

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  async function handleSaveEdit(fields) {
    try {
      await updatePlanCheckLetter(letter.id, fields)
      if (ctx.pcLetterCache) ctx.pcLetterCache[reviewId] = undefined
      setEditOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  async function handleTogglePosted() {
    try {
      await updatePlanCheckLetter(letter.id, { posted: !letter.posted })
      if (ctx.pcLetterCache) ctx.pcLetterCache[reviewId] = undefined
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--section" />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">PC Letter Info</span>
        </div>
        {!readOnly && <ActionMenuButton menu={menu} />}
      </div>
      {!readOnly && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => setEditOpen(true)}>Edit info</ActionMenuItem>
          <ActionMenuItem onClick={handleTogglePosted}>{letter.posted ? 'Mark response not posted' : 'Mark response posted'}</ActionMenuItem>
        </ActionMenu>
      )}
      {editOpen && (
        <LetterFormModal
          title="Edit PC Letter Info"
          initial={letter}
          showResponseDate
          onSave={handleSaveEdit}
          onClose={() => setEditOpen(false)}
        />
      )}
      {!isCollapsed && (
        <div className="wf-children">
          <InfoRow label="Date Received" value={fmtDate(letter.date_received) || '—'} />
          <InfoRow label="Label" value={letter.label || '—'} />
          <InfoRow label="Plan Check Reviewer" value={letter.reviewer_contact_name || '—'} />
          <InfoRow label="Response Date" value={fmtDate(letter.response_letter_date) || '—'} />
          <InfoRow label="Response Posted" value={letter.posted ? 'Yes' : 'No'} />
          {letter.response_letter_url && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                <span className="wf-bullet wf-bullet--item" />
                <div className="wf-text-wrap">
                  <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginRight: 6 }}>
                    Response Letter
                  </span>
                  <a href={letter.response_letter_url} target="_blank" rel="noreferrer"
                     className="wf-node-text" style={{ color: '#2563eb', textDecoration: 'none' }}
                     onClick={e => e.stopPropagation()}>Open PDF ↗</a>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ── PC Letter node ──────────────────────────────────────────────────────── */

function PCLetterNode({ letter, reviewId, projectId }) {
  const key = 'pc-letter:' + letter.id
  const isCollapsed = wf.collapsed[key] !== false
  const isAtLetter = letter._source === 'airtable'
  const menu = useActionMenu()
  const letterLabel = 'PC Letter' + (letter.date_received ? ' ' + fmtDate(letter.date_received) : '')
    + (letter.label ? ' — ' + letter.label : '') + ' 📃'

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  async function handleDelete() {
    if (!(await confirmDialog({ title: 'Delete PC Letter?', message: 'This will also delete all comments on this letter. Continue?', confirmLabel: 'Delete', danger: true }))) return
    try {
      await deletePlanCheckLetter(letter.id)
      if (ctx.pcLetterCache) ctx.pcLetterCache[reviewId] = undefined
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  async function handleEditUrl() {
    const url = await promptDialog({ title: 'Source Letter URL', message: 'Paste the URL of the city\'s comment letter PDF:', defaultValue: letter.source_letter_url || '', placeholder: 'https://…' })
    if (url === null) return
    try {
      await updatePlanCheckLetter(letter.id, { source_letter_url: url || null })
      if (ctx.pcLetterCache) ctx.pcLetterCache[reviewId] = undefined
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  async function handleGenerateResponseLetter() {
    const today = new Date().toISOString().slice(0, 10)
    const responseDate = await promptDialog({
      title: 'Generate Response Letter',
      message: 'Response date (leave blank for today):',
      placeholder: today,
      defaultValue: letter.response_letter_date || '',
    })
    if (responseDate === null) return
    // Open tab synchronously so the browser doesn't block it as a popup,
    // write a loading screen, then navigate to the PDF blob once ready.
    const tab = window.open('', '_blank')
    if (tab) {
      tab.document.write('<body style="margin:0;font-family:\'DM Sans\',sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#f4f7f8;color:#07272D;"><p style="font-size:13px;color:#9ab0ba;letter-spacing:0.01em">Generating response letter…</p></body>')
      tab.document.close()
    }
    try {
      const blob = await generatePCResponsePdf({
        letterId: letter.id,
        source: letter._source || 'supabase',
        projectId,
        reviewId,
        responseDate: responseDate || today,
      })
      const url = URL.createObjectURL(blob)
      if (tab) tab.location = url
      else window.open(url, '_blank')
    } catch (e) {
      if (tab) tab.close()
      alertDialog({ title: 'Error generating PDF', message: e.message })
    }
  }

  const totalComments = (letter.comments || []).length
  const openComments = (letter.comments || []).filter(c => !c.cleared).length

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--ms" onClick={toggleCollapse} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{letterLabel}</span>
          <span className="wf-meta">
            {isAtLetter && (
              <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: 'var(--aj-text-muted)', border: '0.5px solid var(--aj-border-mid)',
                borderRadius: 3, padding: '1px 4px', lineHeight: 1 }}>AT</span>
            )}
            {letter.posted && (
              <span className="wf-status-pill" style={{ color: '#1D9E75', borderColor: '#1D9E75' + '33' }}>✓ Response Posted</span>
            )}
            {totalComments > 0 && (
              <span className="wf-date-btn" style={{ pointerEvents: 'none', color: 'var(--aj-text-muted)' }}>
                {openComments > 0 ? openComments + ' open' : 'all cleared'}
              </span>
            )}
            {letter.source_letter_url && (
              <a href={letter.source_letter_url} target="_blank" rel="noreferrer"
                 className="wf-date-btn" style={{ textDecoration: 'none', color: '#2563eb' }}
                 onClick={e => e.stopPropagation()}>letter ↗</a>
            )}
            {letter.response_letter_url && (
              <a href={letter.response_letter_url} target="_blank" rel="noreferrer"
                 className="wf-date-btn" style={{ textDecoration: 'none', color: '#1D9E75' }}
                 onClick={e => e.stopPropagation()}>response ↗</a>
            )}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        {isAtLetter ? (
          letter.source_letter_url
            ? <ActionMenuItem onClick={() => window.open(letter.source_letter_url, '_blank')}>Open letter ↗</ActionMenuItem>
            : <ActionMenuItem onClick={() => {}}>Read-only (Airtable)</ActionMenuItem>
        ) : (
          <>
            <ActionMenuItem onClick={handleEditUrl}>{letter.source_letter_url ? 'Update letter URL' : 'Set letter URL'}</ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem onClick={handleDelete} danger>Delete letter</ActionMenuItem>
          </>
        )}
        <ActionMenuDivider />
        <ActionMenuItem onClick={handleGenerateResponseLetter}>Generate response letter…</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          <PCLetterInfoSection letter={letter} reviewId={reviewId} />
          <WrittenCommentsSection letter={letter} reviewId={reviewId} projectId={projectId} />
          <RedlinesSection letter={letter} reviewId={reviewId} projectId={projectId} />
        </div>
      )}
    </div>
  )
}

/* ── Link Milestone modal — for deltas with no milestoneId yet (created
   before this linking existed, e.g. via direct Airtable entry). ─────────── */

function LinkMilestoneModal({ delta, reviews, onLinked, onClose }) {
  const [candidates, setCandidates] = useState(null)
  const [showAll, setShowAll] = useState(false)
  const [milestoneId, setMilestoneId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const projectId = ctx.recordId || wf.zoomProjectId || ''

  useEffect(() => {
    getRevisionMilestoneCandidates(projectId, delta.type, reviews).then(res => {
      setCandidates(res)
      setMilestoneId(res.smart[0]?.id || '')
    }).catch(() => setCandidates({ smart: [], all: [] }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  async function handleSave() {
    if (!milestoneId || saving) return
    setSaving(true); setError('')
    try {
      await linkDeltaRevisionToMilestone(delta.type, delta.id, milestoneId)
      onLinked()
    } catch (e) {
      setError(e.message || 'Failed to link milestone.')
      setSaving(false)
    }
  }

  const list = candidates ? (showAll ? candidates.all : candidates.smart) : null
  const canShowAllToggle = candidates && !showAll && candidates.all.length > candidates.smart.length

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)', width: 420, maxWidth: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)' }}>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', flex: 1 }}>
            Link to Milestone
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', padding: 4 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {candidates === null && (
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-muted)' }}>Loading milestones…</div>
          )}
          {candidates !== null && list.length === 0 && (
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-muted)' }}>
              No available revision milestone found{showAll ? '' : ' for this permit\'s track'}.
            </div>
          )}
          {candidates !== null && list.length > 0 && (
            <select value={milestoneId} onChange={e => setMilestoneId(e.target.value)} style={{ ...modalInputStyle, fontFamily: '"DM Sans",sans-serif' }}>
              <option value="">— select a milestone —</option>
              {list.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          )}
          {canShowAllToggle && (
            <button onClick={() => setShowAll(true)} style={{ background: 'none', border: 'none', color: '#2563eb', fontFamily: '"DM Sans",sans-serif', fontSize: 12, cursor: 'pointer', padding: 0, textAlign: 'left' }}>
              Show all revision milestones
            </button>
          )}
          {error && <div style={{ color: '#dc2626', fontFamily: '"DM Sans",sans-serif', fontSize: 12 }}>{error}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '0.5px solid var(--aj-border)' }}>
          <button onClick={onClose} style={{ background: 'transparent', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '8px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', cursor: 'pointer' }}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !milestoneId}
            style={{ background: '#07272D', border: 'none', borderRadius: 4, padding: '8px 20px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, color: 'white', cursor: (saving || !milestoneId) ? 'default' : 'pointer', opacity: (saving || !milestoneId) ? 0.5 : 1 }}
          >
            {saving ? 'Linking…' : 'Link'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/* ── Delta Revision node — expands into its linked milestone + that
   milestone's Drawing Sheets (2026-07). The milestone link is read off the
   Delta's own reverse field (delta.milestoneId, set by createDeltaRevision /
   postMilestone — see docs/PLAN_CHECK.md's P6). Deltas created before that
   linking existed have no milestoneId — an ⋯ menu offers "Link to milestone"
   for those, opening LinkMilestoneModal above. ─────────────────────────── */

function DeltaRevisionNode({ delta, reviews }) {
  const key = 'delta-revision:' + delta.id
  const isCollapsed = wf.collapsed[key] !== false
  const [msInfo, setMsInfo] = useState(null)        // null = not loaded yet
  const [sheetItems, setSheetItems] = useState(null) // null = loading, [] = loaded empty
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const menu = useActionMenu()
  const loadedRef = useRef(false)

  const linkedNames = Array.isArray(reviews) && delta.reviewIds?.length
    ? delta.reviewIds
        .map(id => reviews.find(r => r.id === id))
        .filter(Boolean)
        .map(r => r.agencyType || r.agency || r.name)
    : []

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  useEffect(() => {
    if (isCollapsed || loadedRef.current || !delta.milestoneId) return
    loadedRef.current = true
    getMilestoneBasicInfo(delta.milestoneId).then(info => {
      setMsInfo(info || {})
      if (!info) { setSheetItems([]); return }
      return getMilestoneSheetRevisions(delta.milestoneId).then(setSheetItems)
    }).catch(() => { setMsInfo({}); setSheetItems([]) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsed, delta.milestoneId])

  const milestonePhasePrefix = msInfo && msInfo.id
    ? resolveMilestonePhasePrefix(msInfo.phaseName, msInfo.typeName)
    : null

  const projectId = ctx.recordId || wf.zoomProjectId || ''

  return (
    <div className={`wf-node wf-node--item${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        {delta.milestoneId ? (
          <button className="wf-triangle" onClick={toggleCollapse}>
            <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
          </button>
        ) : (
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        )}
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{delta.revisionLongName || delta.name || 'Delta Revision'}</span>
          <span className="wf-meta">
            {delta.releaseDate && (
              <span className="wf-date-btn" style={{ pointerEvents: 'none', color: 'var(--aj-text-muted)' }}>{fmtDate(delta.releaseDate)}</span>
            )}
            {linkedNames.length > 0 && (
              <span title="Linked agency review(s)" style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)' }}>
                <span style={{ letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginRight: 4 }}>Agency</span>
                {linkedNames.join(', ')}
              </span>
            )}
            {delta.description && delta.description !== 'TBD' && (
              <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>{delta.description}</span>
            )}
          </span>
        </div>
        {!delta.milestoneId && <ActionMenuButton menu={menu} />}
      </div>
      {!delta.milestoneId && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => setLinkModalOpen(true)}>Link to milestone…</ActionMenuItem>
        </ActionMenu>
      )}
      {linkModalOpen && (
        <LinkMilestoneModal
          delta={delta}
          reviews={reviews}
          onLinked={() => {
            setLinkModalOpen(false)
            ctx.permitDeltaCache = undefined
            window.dispatchEvent(new Event('outline-render'))
          }}
          onClose={() => setLinkModalOpen(false)}
        />
      )}
      {!isCollapsed && delta.milestoneId && (
        <div className="wf-children">
          <div className="wf-node wf-node--item">
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
              <button
                className="wf-bullet wf-bullet--ms" title="Zoom into milestone"
                data-fn="zoomTo" data-level="ms" data-msid={delta.milestoneId} data-projid={projectId}
              />
              <div className="wf-text-wrap">
                <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginRight: 6 }}>
                  Milestone
                </span>
                <span className="wf-node-text">{msInfo?.name || (msInfo === null ? 'Loading…' : 'Milestone not found')}</span>
              </div>
            </div>
          </div>
          <DeltaMilestoneDrawingsSection
            deltaId={delta.id}
            msId={delta.milestoneId}
            sheetItems={sheetItems}
            milestonePhasePrefix={milestonePhasePrefix}
            projectId={projectId}
          />
        </div>
      )}
    </div>
  )
}

/* ── Drawing Sheets section under a Delta's linked milestone — mirrors the
   "Drawing Sheets" section MilestoneSections.jsx renders on the milestone
   itself (triangle + bullet + label + count, "Add drawing sheets" on the ⋯
   menu), so this reads as the same node type wherever it's encountered. ─── */

function DeltaMilestoneDrawingsSection({ deltaId, msId, sheetItems, milestonePhasePrefix, projectId }) {
  const key = 'delta-sheets-sec:' + deltaId
  const isCollapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--section" />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Drawing Sheets</span>
          {Array.isArray(sheetItems) && sheetItems.length > 0 && (
            <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{sheetItems.length}</span>
          )}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-add-sheets', { detail: { msId } }))}>
          Add drawing sheets
        </ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          {sheetItems === null && loadingRow()}
          {Array.isArray(sheetItems) && sheetItems.length === 0 && emptyRow('No drawing sheets yet — use ⋯ to add sheets.')}
          {Array.isArray(sheetItems) && sheetItems.length > 0 && (
            <DrawingSheetList
              sheets={sheetItems.map(s => {
                const rawTitle = (first(s.fields['Sheet Number and Title (from Drawing Sheet)']) || '').trim()
                const dashIdx = rawTitle.indexOf(' - ')
                return {
                  id:           first(s.fields['Drawing Sheet']) || s.id,
                  revisionId:   s.id,
                  revision:     s,
                  title:        rawTitle || 'Untitled',
                  sheetNumber:  dashIdx !== -1 ? rawTitle.slice(0, dashIdx).trim() : rawTitle,
                  sheetTitle:   dashIdx !== -1 ? rawTitle.slice(dashIdx + 3).trim() : '',
                  progress:     '',
                }
              })}
              collapseKeyPrefix={'delta-sheets:' + deltaId}
              context="milestone"
              milestonePhasePrefix={milestonePhasePrefix}
              currentMemberId={wf.currentMemberId}
              milestoneId={msId}
              projectId={projectId}
            />
          )}
        </div>
      )}
    </div>
  )
}

/* ── Stakeholder Input row ───────────────────────────────────────────────── */

function StakeholderRow({ input, reviewId }) {
  const menu = useActionMenu()

  function bustCache() {
    if (ctx.stakeholderCache) ctx.stakeholderCache[reviewId] = undefined
    window.dispatchEvent(new Event('outline-render'))
  }

  const title = [
    input.input_type || 'Input',
    input.source_name ? '— ' + input.source_name : '',
  ].filter(Boolean).join(' ')

  async function handleEdit() {
    const date = await promptDialog({ title: 'Edit Stakeholder Input', message: 'Date received (YYYY-MM-DD):', defaultValue: input.date_received || '' })
    if (date === null) return
    const type = await promptDialog({ title: 'Edit Stakeholder Input', message: 'Type (Public Comment, HOA, Design Review Board, Planning Commission, City Council, Neighbor, Other):', defaultValue: input.input_type || '' })
    if (type === null) return
    const sourceName = await promptDialog({ title: 'Edit Stakeholder Input', message: 'Source name:', defaultValue: input.source_name || '' })
    if (sourceName === null) return
    const sourceOrg = await promptDialog({ title: 'Edit Stakeholder Input', message: 'Organization (optional):', defaultValue: input.source_organization || '' })
    if (sourceOrg === null) return
    const summary = await promptDialog({ title: 'Edit Stakeholder Input', message: 'Summary:', defaultValue: input.summary || '', multiline: true })
    if (summary === null) return
    const response = await promptDialog({ title: 'Edit Stakeholder Input', message: 'Our response / how addressed:', defaultValue: input.response_text || '', multiline: true })
    if (response === null) return
    try {
      await updateStakeholderInput(input.id, {
        date_received: date || null,
        input_type: type || null,
        source_name: sourceName || null,
        source_organization: sourceOrg || null,
        summary: summary || null,
        response_text: response || null,
      })
      bustCache()
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  async function handleToggleAddressed() {
    try {
      await updateStakeholderInput(input.id, { addressed: !input.addressed })
      bustCache()
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  async function handleDelete() {
    if (!(await confirmDialog({ title: 'Delete Input?', message: 'Delete this stakeholder input?', confirmLabel: 'Delete', danger: true }))) return
    try {
      await deleteStakeholderInput(input.id)
      bustCache()
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row" style={{ alignItems: 'flex-start' }}>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ marginTop: 5 }} />
        <div className="wf-text-wrap" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 0 }}>
            <span className="wf-node-text" style={{ flex: 1 }}>{title}</span>
            {input.date_received && <span className="wf-date-btn" style={{ pointerEvents: 'none', color: 'var(--aj-text-muted)' }}>{fmtDate(input.date_received)}</span>}
            {input.addressed && <span className="wf-status-pill" style={{ color: '#1D9E75', borderColor: '#1D9E75' + '33' }}>✓ Addressed</span>}
          </div>
          {input.summary && (
            <div className="wf-node-text" style={{ fontSize: 11, color: 'var(--aj-text-mid)', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.45, marginTop: 2 }}>
              {input.summary}
            </div>
          )}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={handleEdit}>Edit</ActionMenuItem>
        <ActionMenuItem onClick={handleToggleAddressed}>{input.addressed ? 'Mark not addressed' : 'Mark addressed'}</ActionMenuItem>
        {input.document_url && <ActionMenuItem onClick={() => window.open(input.document_url, '_blank')}>Open document</ActionMenuItem>}
        <ActionMenuDivider />
        <ActionMenuItem onClick={handleDelete} danger>Delete</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/* ── Shared PC data loader (Airtable letters + Supabase letters/stakeholder) ── */

async function loadAllPCDataForReview(reviewId) {
  const [{ letters: supaLetters, stakeholderInputs }, atLetters, intakeResult] = await Promise.all([
    getPlanCheckData(reviewId),
    getAirtableLettersForReview(reviewId),
    getIntakeForReview(reviewId).catch(() => ({ intake: null })),
  ])
  const atLettersWithComments = await Promise.all(
    atLetters.map(async l => ({
      ...l,
      comments: l._responseIds?.length
        ? await getAirtableCommentsForLetter(l._responseIds).catch(() => [])
        : [],
    }))
  )
  const allLetters = [...atLettersWithComments, ...supaLetters].sort((a, b) =>
    (a.date_received || '').localeCompare(b.date_received || '')
  )
  if (!ctx.pcLetterCache)  ctx.pcLetterCache  = {}
  if (!ctx.stakeholderCache) ctx.stakeholderCache = {}
  if (!ctx.pcIntakeCache)  ctx.pcIntakeCache  = {}
  ctx.pcLetterCache[reviewId]  = allLetters
  ctx.stakeholderCache[reviewId] = stakeholderInputs
  ctx.pcIntakeCache[reviewId]  = intakeResult.intake || null  // null = no active intake
  window.dispatchEvent(new Event('outline-render'))
}

/* ═══════════════════════════════════════════════════════════════════════════
   Exported section components
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Delta Revisions (Airtable, read-only) — mounted on the PERMIT ──────────
   A single component parameterized by Type, used for both "Plan Check
   Revisions" (lettered, agency-comment-driven) and "Permit Revisions"
   (numbered, post-permit / construction-driven). Deltas are fetched once per
   project (ctx.permitDeltaCache) and filtered here by permitId + type — never
   fetched per-review, since one delta can span multiple Agency Reviews. */

export function DeltaRevisionsSection({ permitId, deltas, reviews, type, label, collapseKeyPrefix }) {
  const key = collapseKeyPrefix + ':' + permitId
  const isCollapsed = wf.collapsed[key] !== false
  const myDeltas = Array.isArray(deltas) ? deltas.filter(d => d.permitId === permitId && d.type === type) : deltas
  const menu = useActionMenu()

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  function handleAdd() {
    window.dispatchEvent(new CustomEvent('open-add-revision-modal', { detail: { permitId, type } }))
  }

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--section" />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{label}</span>
          {Array.isArray(myDeltas) && myDeltas.length > 0 && (
            <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{myDeltas.length}</span>
          )}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={handleAdd}>Add {label.replace(/s$/, '')}</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          {(myDeltas === null || myDeltas === undefined) && loadingRow()}
          {Array.isArray(myDeltas) && myDeltas.length === 0 && emptyRow('No ' + label.toLowerCase() + ' yet.')}
          {Array.isArray(myDeltas) && myDeltas.map(d => <DeltaRevisionNode key={d.id} delta={d} reviews={reviews} />)}
        </div>
      )}
    </div>
  )
}

/* ── Intake resume node ──────────────────────────────────────────────────── */

function IntakeResumeNode({ intake, onResume, onDiscard }) {
  const menu = useActionMenu()
  const count    = (intake.items || []).length
  const included = (intake.items || []).filter(it => it.include).length
  const updated  = intake.updated_at || intake.created_at
  const dateStr  = updated ? new Date(updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null

  return (
    <div className="wf-node wf-node--item" onClick={onResume} style={{ cursor: 'pointer' }}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ background: '#d97706' }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: 'var(--aj-text)' }}>Intake in progress</span>
          {count > 0 && (
            <span className="wf-prog" style={{ color: '#d97706', marginLeft: 8 }}>
              {included}/{count} selected
            </span>
          )}
          {dateStr && (
            <span className="wf-date-btn" style={{ pointerEvents: 'none', color: 'var(--aj-text-muted)' }}>
              {dateStr}
            </span>
          )}
          <span
            style={{
              display: 'inline-block', marginLeft: 8,
              fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600,
              color: '#d97706', letterSpacing: '0.02em',
            }}
          >
            Resume →
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={e => { e.stopPropagation(); onResume() }}>Resume intake</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={e => { e.stopPropagation(); onDiscard() }} danger>Discard intake</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/* ── Agency Correspondence (PC Letters + comments, Supabase) ─────────────── */

export function AgencyCorrespondenceSection({ review }) {
  const reviewId = review.id
  const key = 'permit-correspondence:' + reviewId
  const isCollapsed = wf.collapsed[key] !== false
  const letters      = ctx.pcLetterCache?.[reviewId]
  const activeIntake = ctx.pcIntakeCache?.[reviewId]   // null=none, object=in-progress
  const menu = useActionMenu()
  const [intakeOpen, setIntakeOpen] = useState(false)
  const [addLetterOpen, setAddLetterOpen] = useState(false)

  function loadPCData() {
    if (!ctx.pcLetterCache)  ctx.pcLetterCache  = {}
    if (!ctx.stakeholderCache) ctx.stakeholderCache = {}
    if (!ctx.pcIntakeCache)  ctx.pcIntakeCache  = {}
    if (ctx.pcLetterCache[reviewId] === null) return  // in-flight
    ctx.pcLetterCache[reviewId] = null
    ctx.stakeholderCache[reviewId] = null
    loadAllPCDataForReview(reviewId).catch(() => {
      if (!ctx.pcLetterCache) ctx.pcLetterCache = {}
      if (!ctx.stakeholderCache) ctx.stakeholderCache = {}
      ctx.pcLetterCache[reviewId] = []
      ctx.stakeholderCache[reviewId] = []
      window.dispatchEvent(new Event('outline-render'))
    })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isCollapsed) loadPCData() }, [isCollapsed, reviewId])

  useEffect(() => {
    function onRender() {
      if (isCollapsed) return
      if (ctx.pcLetterCache?.[reviewId] === undefined) loadPCData()
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [reviewId, isCollapsed]) // eslint-disable-line react-hooks/exhaustive-deps

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  async function handleSaveAddLetter(fields) {
    try {
      await createPlanCheckLetter(reviewId, fields)
      if (ctx.pcLetterCache) ctx.pcLetterCache[reviewId] = undefined
      setAddLetterOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  function handleIntakeCommitted() {
    if (ctx.pcLetterCache)  ctx.pcLetterCache[reviewId]  = undefined
    if (ctx.pcIntakeCache)  ctx.pcIntakeCache[reviewId]  = null
    window.dispatchEvent(new Event('outline-render'))
  }

  async function handleIntakeDiscard() {
    if (!activeIntake) return
    const confirmed = await (async () => {
      const { confirmDialog } = await import('./Dialog.jsx')
      return confirmDialog({ title: 'Discard intake?', message: 'This will permanently delete the in-progress intake. The extracted comments will be lost.', confirmLabel: 'Discard', danger: true })
    })()
    if (!confirmed) return
    try {
      await discardPCIntake(activeIntake.id)
      if (ctx.pcIntakeCache) ctx.pcIntakeCache[reviewId] = null
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) {
      const { alertDialog } = await import('./Dialog.jsx')
      alertDialog({ title: 'Error', message: e.message })
    }
  }

  const totalOpen = Array.isArray(letters)
    ? letters.reduce((n, l) => n + (l.comments || []).filter(c => !c.cleared).length, 0)
    : null

  return (
    <>
      <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
        <div className="wf-row">
          <button className="wf-triangle" onClick={toggleCollapse}>
            <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
          </button>
          <button className="wf-bullet wf-bullet--section" />
          <div className="wf-text-wrap">
            <span className="wf-node-text wf-node-text--section">Agency Correspondence</span>
            {Array.isArray(letters) && letters.length > 0 && (
              <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{letters.length}</span>
            )}
            {totalOpen !== null && totalOpen > 0 && (
              <span className="wf-date-btn" style={{ pointerEvents: 'none', color: '#d97706' }}>{totalOpen} open</span>
            )}
          </div>
          <ActionMenuButton menu={menu} />
        </div>
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => setIntakeOpen(true)}>AI intake letter…</ActionMenuItem>
          <ActionMenuDivider />
          <ActionMenuItem onClick={() => setAddLetterOpen(true)}>Add PC Letter manually</ActionMenuItem>
        </ActionMenu>
        {!isCollapsed && (
          <div className="wf-children">
            {activeIntake && (
              <IntakeResumeNode
                intake={activeIntake}
                onResume={() => setIntakeOpen(true)}
                onDiscard={handleIntakeDiscard}
              />
            )}
            {(letters === null || letters === undefined) && loadingRow()}
            {Array.isArray(letters) && letters.length === 0 && !activeIntake && emptyRow('No letters yet. Use the ⋯ menu to add one or run AI intake.')}
            {Array.isArray(letters) && letters.map(l => (
              <PCLetterNode key={l.id} letter={l} reviewId={reviewId} projectId={ctx.recordId} />
            ))}
          </div>
        )}
      </div>
      {intakeOpen && (
        <PlanCheckIntakeModal
          reviewId={reviewId}
          projectId={ctx.recordId}
          onClose={() => setIntakeOpen(false)}
          onCommitted={handleIntakeCommitted}
        />
      )}
      {addLetterOpen && (
        <LetterFormModal
          title="Add PC Letter"
          initial={{}}
          showResponseDate={false}
          onSave={handleSaveAddLetter}
          onClose={() => setAddLetterOpen(false)}
        />
      )}
    </>
  )
}

/* ── Stakeholder Input (Supabase) ────────────────────────────────────────── */

export function StakeholderInputSection({ review }) {
  const reviewId = review.id
  const key = 'permit-stakeholder:' + reviewId
  const isCollapsed = wf.collapsed[key] !== false
  const inputs = ctx.stakeholderCache?.[reviewId]
  const menu = useActionMenu()

  function loadStakeholderData() {
    if (!ctx.pcLetterCache) ctx.pcLetterCache = {}
    if (!ctx.stakeholderCache) ctx.stakeholderCache = {}
    // If letter data is already in-flight, the stakeholder data will come with it
    if (ctx.pcLetterCache[reviewId] === null) return
    ctx.pcLetterCache[reviewId] = null
    ctx.stakeholderCache[reviewId] = null
    loadAllPCDataForReview(reviewId).catch(() => {
      if (!ctx.pcLetterCache) ctx.pcLetterCache = {}
      if (!ctx.stakeholderCache) ctx.stakeholderCache = {}
      ctx.pcLetterCache[reviewId] = []
      ctx.stakeholderCache[reviewId] = []
      window.dispatchEvent(new Event('outline-render'))
    })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isCollapsed) loadStakeholderData() }, [isCollapsed, reviewId])

  useEffect(() => {
    function onRender() {
      if (isCollapsed) return
      if (ctx.stakeholderCache?.[reviewId] === undefined) loadStakeholderData()
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [reviewId, isCollapsed]) // eslint-disable-line react-hooks/exhaustive-deps

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  async function handleAddInput() {
    const date = await promptDialog({ title: 'Add Stakeholder Input', message: 'Date received (YYYY-MM-DD):', placeholder: new Date().toISOString().slice(0, 10) })
    if (date === null) return
    const type = await promptDialog({ title: 'Add Stakeholder Input', message: 'Type:', placeholder: 'Public Comment, HOA, Design Review Board, Planning Commission, City Council, Neighbor, Other' })
    if (type === null) return
    const sourceName = await promptDialog({ title: 'Add Stakeholder Input', message: 'Source name (person or organization):' })
    if (sourceName === null) return
    const summary = await promptDialog({ title: 'Add Stakeholder Input', message: 'Summary of input:', multiline: true })
    if (summary === null) return
    try {
      await createStakeholderInput(reviewId, {
        date_received: date || null,
        input_type: type || null,
        source_name: sourceName || null,
        summary: summary || null,
      })
      if (ctx.stakeholderCache) ctx.stakeholderCache[reviewId] = undefined
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Error', message: e.message }) }
  }

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--section" />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Stakeholder Input</span>
          {Array.isArray(inputs) && inputs.length > 0 && (
            <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{inputs.length}</span>
          )}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={handleAddInput}>Add stakeholder input</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          {(inputs === null || inputs === undefined) && loadingRow()}
          {Array.isArray(inputs) && inputs.length === 0 && emptyRow('No stakeholder input logged yet.')}
          {Array.isArray(inputs) && inputs.map(inp => (
            <StakeholderRow key={inp.id} input={inp} reviewId={reviewId} />
          ))}
        </div>
      )}
    </div>
  )
}
